import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type Props<T extends string> = {
  value: T;
  // ReactNode so a segment can be an icon (e.g. Explore's Captures camera), not just text.
  // `title` shows a hover tooltip (e.g. why a segment is disabled).
  options: { value: T; label: ReactNode; disabled?: boolean; title?: string }[];
  onChange: (value: T) => void;
  ariaLabel: string;
};

/** Small segmented control matching the date-preset chips (kg/cost, day/week). */
export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: Props<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5"
    >
      {options.map((opt) => {
        const button = (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            disabled={opt.disabled}
            aria-pressed={value === opt.value}
            className={cn(
              "rounded px-2 py-1 text-xs font-medium transition-colors",
              opt.disabled
                ? // pointer-events-none lets the hover reach the titled wrapper below
                  // (a disabled <button> swallows hover, so the tooltip wouldn't show).
                  "pointer-events-none text-gray-300"
                : value === opt.value
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
            )}
            data-testid={`toggle-${opt.value}`}
          >
            {opt.label}
          </button>
        );
        if (!opt.title) return button;
        return (
          <span
            key={opt.value}
            title={opt.title}
            className={cn("inline-flex", opt.disabled && "cursor-not-allowed")}
          >
            {button}
          </span>
        );
      })}
    </div>
  );
}
